import { useEffect, useState } from 'react'
import { leagueKoOptions, positionTranslate } from 'src/locales/constants'
import { Agency, Brand, Personal, State, StateObj } from 'src/types'
import { translatedBaseSettings, translatedColorSettings, translatedEmbroideries } from '../helper'
import { genFrontImgFromCanvas } from 'src/features/generatePdf/helper/generateImages/front'
import axios from 'axios'

export const useRetailInformation = (retailEmail: string) => {
  const [retailSelectableBrands, setRetailSelectableBrands] = useState<StateObj[]>([{ value: '', label: '' }])
  const fetchRetailInformation = async () => {
    await axios.post('/api/server/retail/information', { email: retailEmail }).then((res) => setRetailSelectableBrands(res.data.selectableBrands))
  }
  useEffect(() => {
    !!retailEmail && fetchRetailInformation()
  }, [retailEmail])
  const selectableBrandChecker = (brand) => retailSelectableBrands[0].value.length > 0 && retailSelectableBrands.map((b) => b.value).includes(brand)
  return {
    selectableBrandChecker
  }
}

export const contentsChecker = (stateObj: StateObj) => {
  const isUsContents = stateObj.label.charCodeAt(0) < 256
  const isKoreaContents = stateObj.label.charCodeAt(0) >= 40001
  return { isUsContents, isKoreaContents }
}

export const sendEmailConfirmMessage = (agency: Agency, isJa: boolean, isEn: boolean): string => {
  if (isEn) {
    return `Do you want to place an order with ${agency.name} ?`
  }
  if (isJa) {
    return `${agency.name}に発注しますか？`
  }
  return `${agency.name}에게 발주하시겠습니까?`
}

const brandNameIdentifier = (brand: Brand) => {
  switch (brand) {
    case 'atoms':
      return 'ATOMS'
    case 'yellStory':
      return 'Yell-Story'
    case 'dxm':
      return 'DxM'
    case 'ragdelion':
      return 'RAG de Lion'
    case 'gorilla':
      return 'ごりら印の野球用具'
  }
}

const payloadSubject = (brand: Brand, isJa, isEn): string => {
  const brandName = brandNameIdentifier(brand)
  if (isJa) {
    return `【${brandName} 発注】`
  }
  if (isEn) {
    return `【アメリカ_${brandName} 発注 】`
  }
  return `【韓国_${brandName} 発注】`
}
const payloadRagLionLogo = (state: State) => {
  const isRagDeLion = state.baseModel.brand === 'ragdelion'
  if (!isRagDeLion) return false
  const unnecessaryColor = state.ragLionLogo.value === 'none'
  const color = unnecessaryColor ? '--' : state.ragLionLogoColor.label
  return {
    label: state.ragLionLogo.label,
    color
  }
}
const payloadPosition = (isKoreaContents: boolean, personal: Personal): string => {
  if (!isKoreaContents) return personal.position as string
  // @ts-ignore
  const koreaPosition = personal.position.includes(',') ? personal.position.split(',') : personal.position
  const position = koreaPosition.map((p) => positionTranslate.find((o) => o.korea === p)?.japanese).toString()
  return position
}
const payloadLeague = (isKoreaContents: boolean, personal: Personal): string => {
  if (!isKoreaContents) return personal.league
  return leagueKoOptions.find((o) => o.label === personal.league)?.value || ''
}

export const payloadGenerator = (
  state: State,
  pdfBase64: string,
  locale: any,
  retailEmail: string,
  retailName: string,
  agency: Agency,
  savedId: string
) => {
  const { isUsContents, isKoreaContents } = contentsChecker(state.dominantArm)
  const { colorSettings1, colorSettings2 } = translatedColorSettings(state, isUsContents, isKoreaContents)
  const baseSettings = translatedBaseSettings(state, isUsContents, isKoreaContents)
  const embroideries = translatedEmbroideries(state.embroideries, isUsContents, isKoreaContents)
  const imageUrlFront = genFrontImgFromCanvas(state, state.baseModel.brand, state.baseModel.position)
  const ragLionLogo = payloadRagLionLogo(state)
  const league = payloadLeague(isKoreaContents, state.personal)
  const position = payloadPosition(isKoreaContents, state.personal)
  const subject = payloadSubject(state?.baseModel?.brand, locale.isJa, locale.isEn)
  const canvas = document.getElementById('canvasBack') as HTMLCanvasElement
  return {
    subject,
    agencyEmail: agency.email,
    retailEmail,
    retailName,
    savedId,
    imageUrlFront,
    imageUrlBack: canvas.toDataURL(),
    baseSettings,
    colorSettings1,
    colorSettings2,
    embroideries,
    ragLionLogo,
    proModelEmbroideries: false,
    orderer: {
      name: state.personal?.userName || '',
      kana: state.personal?.userNameKana || '',
      mailAddress: state.personal?.mailAddress || '',
      phoneNumber: state.personal?.phoneNumber || '',
      league,
      position,
      remarks: state.personal?.remarks || '-'
    },
    locale: locale.locale,
    pdfBase64
  }
}

export const useRetailNameResolver = (retailEmail: string): { isFetched: boolean; retailName: string } => {
  const [retailName, setRetailName] = useState(null)
  const [isFetched, setIsFetched] = useState<boolean>(false)
  useEffect(() => {
    const fetching = async () => {
      const res = await axios.post('/api/server/retail/retailName', { email: retailEmail })
      setRetailName(res.data.retailName)
      setIsFetched(true)
    }
    fetching()
  }, [])
  return { isFetched, retailName }
}
